import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Breadcrumb } from '../../common/atoms';
import { PeriodPicker } from '../../common/molecules';
import { successOrFailureToaster } from '../../common/atoms/messages';
import { utilizationApi } from '../../../services';
import { handleAppError } from '../../handlers';
import { ContractsManager } from '../../../managers';
import { EmptyState, SkeletonCard } from '../../tailwind/atoms';
import DateRangePicker from '../../tailwind/molecules/DateRangePicker';
import { SALES_ROLE } from '../../../constants/overview';
import { dsButtonGhostBrand, dsButtonPrimary, joinButtonClasses } from '../../../utils/buttonStyles';
import { Caveat, Feedback, FollowUp, Hero, Medicines, Safety, When, Who, Why } from './sections';
import { TipLayer } from './parts';

// Below this many visits in the period, the page shows no charts
const MIN_VISITS = 10;
let token = '';

/**
 * Utilization › OHC: one short report on the visits to the company's onsite health centre. The backend
 * computes every number and writes the sentences from them (headline, each section's finding and "What the
 * numbers say"); this page lays them out like Health analytics. Download report prints the same page as a PDF.
 */
class OhcUtilization extends Component {
  state = {
    contracts: [],
    selectedContractObj: undefined,
    contractId: null,
    filterDates: { startDate: null, endDate: null },
    page: null,
    loading: true,
    contractsFailed: false,
    downloading: false
  };

  componentDidMount() {
    this.fetchContractDetails();
  }

  componentWillUnmount() {
    token && token.cancel();
  }

  componentDidUpdate(prevProps, prevState) {
    const { contractId, filterDates } = this.state;
    if ((contractId && prevState.contractId !== contractId) ||
      (filterDates.startDate && prevState.filterDates.startDate !== filterDates.startDate) ||
      (filterDates.endDate && prevState.filterDates.endDate !== filterDates.endDate)) {
      token && token.cancel();
      this.fetchCompleteData();
    }
  }

  fetchContractDetails = () => {
    const contractId = parseInt(this.props.location.state?.contractId, 10);
    utilizationApi.fetchContracts(
      {},
      (resp) => {
        // No "Overall" entry for this type: the OHC report is per contract period.
        const formattedData = new ContractsManager(resp.data, 'OHC').formattedData;
        const selectedContractObj = formattedData.find(e => e.contract_id === contractId) ?? formattedData[0];
        this.setState({
          contracts: formattedData,
          selectedContractObj,
          contractId: selectedContractObj?.contract_id,
          loading: !!selectedContractObj,
          filterDates: { startDate: selectedContractObj?.start_date, endDate: selectedContractObj?.end_date }
        });
      },
      (error) => {
        this.setState({ loading: false, contractsFailed: true });
        handleAppError(error);
      }
    );
  };

  period = () => {
    const { filterDates, selectedContractObj } = this.state;
    return {
      contractId: this.state.contractId,
      startDate: moment(filterDates.startDate || selectedContractObj.start_date).format('YYYY-MM-DD'),
      endDate: moment(filterDates.endDate || selectedContractObj.end_date).format('YYYY-MM-DD')
    };
  };

  fetchCompleteData = () => {
    token = axios.CancelToken.source();
    this.setState({ loading: true, page: null });
    const options = { ...this.period(), cancelToken: token.token };
    utilizationApi.ohcFetchPage(options)
      .then((response) => this.setState({ page: response.data, loading: false }))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        this.setState({ loading: false });
        handleAppError(error);
      });
  };

  downloadReport = () => {
    this.setState({ downloading: true });
    utilizationApi.ohcDownloadReport(
      this.period(),
      (res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const fileName = (res.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/)?.[1] || 'OHC_Utilization.pdf';
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        this.setState({ downloading: false });
      },
      () => {
        this.setState({ downloading: false });
        successOrFailureToaster({ message: 'Could not download the OHC report. Please try again.', type: 'fail' });
      }
    );
  };

  changeContractId = (contractId) => {
    const newContract = this.state.contracts.find(e => e.contract_id === parseInt(contractId, 10));
    if (!newContract || newContract.contract_id === this.state.contractId) return;

    this.setState({
      contractId: newContract.contract_id,
      selectedContractObj: newContract,
      page: null,
      loading: true,
      filterDates: { startDate: newContract.start_date, endDate: newContract.end_date }
    });
  };

  changeDates = (e, dateType) => {
    const { selectedContractObj, filterDates } = this.state;
    if (dateType === 'reset' || !e) {
      const fallback = dateType === 'startDate' ? selectedContractObj.start_date : selectedContractObj.end_date;
      if (dateType === 'reset') {
        this.setState({ filterDates: { startDate: selectedContractObj.start_date, endDate: selectedContractObj.end_date } });
      } else {
        this.setState({ filterDates: { ...filterDates, [dateType]: moment(fallback).format('YYYY-MM-DD') } });
      }
      return;
    }
    this.setState({ filterDates: { ...filterDates, [dateType]: moment(e).format('YYYY-MM-DD') } });
  };

  render() {
    const { contracts, selectedContractObj, loading, contractsFailed, downloading, page } = this.state;
    const visits = page?.metrics?.hero?.visits || 0;
    const noData = !loading && visits === 0;
    const sections = (page?.text?.sections || []).reduce((acc, section) => ({ ...acc, [section.id]: section }), {});
    const orgName = sessionStorage.getItem('company_name') || '';

    return (
      <div className='ds-report -mx-4 -my-6 min-h-full bg-gray-100 px-ds-6 pb-16 pt-ds-8 sm:-mx-6 sm:-my-8 lg:px-12 lg:pb-24 lg:pt-11'>
        <div className='mx-auto max-w-[1120px]'>
          <div className='mb-ds-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
            <Breadcrumb
              list={[
                { text: 'Utilization', link: '/utilization/ahc' },
                { text: 'Occupational Health Centre', link: '/utilization/ohc' }
              ]}
            />
            <div className='flex shrink-0 flex-wrap items-end gap-2 lg:justify-end'>
              <PeriodPicker
                list={contracts.map(e => e.text)}
                ids={contracts.map(e => e.contract_id)}
                loading={!selectedContractObj}
                newPageId={selectedContractObj ? selectedContractObj.contract_id : ''}
                changeHandler={this.changeContractId}
              />
              <button
                type='button'
                onClick={this.downloadReport}
                disabled={loading || downloading || noData || SALES_ROLE === 'sales'}
                className={joinButtonClasses(dsButtonGhostBrand, 'h-10 px-5', (noData || SALES_ROLE === 'sales') && 'cursor-not-allowed')}
              >
                {downloading ? 'Preparing report…' : 'Download report'}
              </button>
              {/* Same button, place and look as the other utilization pages */}
              <button
                type='button'
                onClick={() => this.props.history.push('/utilization/ohc/activity-log', this.period())}
                disabled={!selectedContractObj || SALES_ROLE === 'sales'}
                className={joinButtonClasses(dsButtonPrimary, 'h-10 px-5', SALES_ROLE === 'sales' && 'cursor-not-allowed')}
              >
                Activity Log
              </button>
            </div>
          </div>
          {selectedContractObj && (
            <div className='-mt-ds-4 mb-ds-8'>
              <DateRangePicker
                isCurrent={selectedContractObj.is_running_contract}
                isOverAll={false}
                filterDates={this.state.filterDates}
                contractDates={[selectedContractObj.start_date, selectedContractObj.end_date]}
                changeDates={this.changeDates}
              />
            </div>
          )}

          {loading ? (
            <div className='grid gap-ds-5'>
              <SkeletonCard className='h-[260px] bg-white' />
              <SkeletonCard className='h-[380px] bg-white' />
            </div>
          ) : null}

          {!loading && contractsFailed ? (
            <EmptyState title='Could not load the OHC report' description='Please reload the page to try again.' />
          ) : null}

          {!loading && !selectedContractObj && !contractsFailed ? (
            <EmptyState title='No OHC period available' description='This company has no contract with an OHC, so there is nothing to report on yet.' />
          ) : null}

          {!loading && selectedContractObj && !page ? (
            <EmptyState title='Could not load the OHC report' description='Please try selecting the period again.' />
          ) : null}

          {!loading && page && !page.enabled ? (
            <EmptyState title={`No OHC for ${orgName || 'this company'}`}
              description='This page shows once the company has an onsite health centre run by ekincare.' />
          ) : null}

          {!loading && page?.enabled && visits < MIN_VISITS ? (
            <EmptyState title='Not enough visits yet'
              description={`The OHC page fills in once ${MIN_VISITS} visits are logged in this period${visits ? `; ${visits} so far` : ''}.`} />
          ) : null}

          {!loading && page?.enabled && visits >= MIN_VISITS ? (
            <TipLayer>
              <div className='ds-anim-drop-in'>
                <Hero orgName={orgName} metrics={page.metrics} text={page.text} />
                <When metrics={page.metrics} text={sections.when} />
                <Who metrics={page.metrics} text={sections.who} />
                <Why metrics={page.metrics} text={sections.why} />
                <Safety metrics={page.metrics} text={sections.safety} />
                <FollowUp metrics={page.metrics} text={sections.followup} />
                <Medicines metrics={page.metrics} text={sections.medicines} />
                <Feedback metrics={page.metrics} text={sections.feedback} />
                <Caveat caveat={page.text.caveat} />
              </div>
            </TipLayer>
          ) : null}
        </div>
      </div>
    );
  }
}

export default OhcUtilization;
